import { stripe } from "../../components/payments/stripeInterface";
import { _t } from "@bark/bark-core";
import {
	confirmPaymentIntent,
	initPaymentIntent, initSetupIntent,
	showModalErrorText
} from "../../components/payments/credit-card";
import {setDefaultPaymentMethod} from "./helpers";

/**
 * Asynchronous payment success callback
 *
 * @callback paymentSuccessCallback
 * @param {StripeToken|null} token A stripe token if going via a token route. Most routes sent this property
 */

/**
 * @callback paymentRequestSuccessHandler
 * @param {string} completedPaymentIntentId
 * @param {string} paymentMethodId
 * @param {InitPaymentIntentResponse} initPaymentIntentResponse
 * @param {ConfirmPaymentIntentResponse} confirmPaymentIntentResponse
 * @param {'apple_pay'|'google_pay'|'paypal'} paymentMethodType
 * @returns Promise<void>
 */

/**
 * @callback walletSetupRequestSuccessHandler
 * @param {string|false} setupIntentId
 */

/**
 * @callback paymentRequestErrorHandler
 */

/**
 * @typedef PaymentRequestWalletsEnabled
 * @property {true} enableWallets
 * @property {PaymentRequestOptions} paymentOptions
 */

/**
 * @typedef PaymentRequestWalletsDisabled
 * @property {false} enableWallets
 */

/**
 * @typedef confirmSetupIntentResponse
 * returns {string} A PaymentMethod ID
 */

/**
 * @typedef PaymentRequestOptions
 * @property {string} label
 * @property {int} amount The amount in the currency's subunit (e.g. pence, cents, yen, etc.), excluding tax
 * @property {boolean} isUpsell
 * @property {boolean} isOnlySavingCard
 * @property {number} cpId
 * @property {number} userId
 * @property {number} nResponses
 * @property {string} code
 * @property {number} projectId
 * @property {string} packType
 * @property {object} disableWallets
 * @property {boolean} disableWallets.googlePay
 * @property {boolean} disableWallets.applePay
 * @property {boolean} disableWallets.paypal
 * @property {paymentRequestSuccessHandler?} onWalletPaymentComplete
 * @property {walletSetupRequestSuccessHandler?} onWalletSetupComplete
 * @property {paymentRequestErrorHandler?} onError
 */

/**
 * @typedef InitPaymentIntentResponse
 * @property {string} clientSecret
 * @property {string} price
 * @property {Number} nCredits
 * @property {Number} nResponses
 * @property {Number} invoiceId
 */

/**
 * @typedef InitSetupIntentResponse
 * @property {boolean} supportsIntents
 * @property {string} setupIntentId
 */

/**
 * @typedef ConfirmPaymentIntentResponse
 * @property {number} invoice_id
 * @property {number} price
 * @property {number} credits
 * @property {number} credit_balance
 * @property {number} cpId
 * @property {boolean} is_paid
 * @property {number} n_responses
 * @property {boolean} is_coupon_applied
 * @property {number} vat_rate
 * @property {string} code
 */

export const expressCheckoutSuccessHandler = async ({$modal, chosenPaymentMethod, options, event, elements}) => {
	let confirmAttempted = false;

	try {
		$modal.modal('hide');
		$('.modal-backdrop').removeClass('modal-backdrop-blue');
		Bark.showLoading();

		let result, initPaymentIntentResponse, setupIntentId;

		if (options.paymentOptions.isOnlySavingCard) {
			const setupAttempt = await attemptWalletSetup(event, elements, options);
			setupIntentId = setupAttempt.setupIntentId;
			result = setupAttempt.result;
		} else {
			const paymentAttempt = await attemptWalletPayment(event, elements, options);
			result = paymentAttempt.result;
			initPaymentIntentResponse = paymentAttempt.initPaymentIntentResponse;
		}

		confirmAttempted = true;

		if (result?.paymentIntent) {
			const confirmResult = await confirmPaymentIntent(
				event.expressPaymentType,
				result.paymentIntent.id,
				options.paymentOptions.userId,
			);

			Bark.hideLoading();

			if (confirmResult.is_paid) {
				await options.paymentOptions.onWalletPaymentComplete?.(
					result.paymentIntent.id,
					result.paymentMethod,
					initPaymentIntentResponse,
					confirmResult,
					chosenPaymentMethod
				);
			}
		} else if (result?.setupIntent) {
			Bark.hideLoading();

			setDefaultPaymentMethod(options.paymentOptions.userId, result.setupIntent.payment_method)

			await options.paymentOptions.onWalletSetupComplete?.(
				result.payment_method
			);
		} else if (result.error) {
			Bark.hideLoading();
			$modal.modal('show');
			showModalErrorText($modal, _t('seller_common:sofort-payment.error.invalid-request'));
			options.paymentOptions?.onError?.();
		}
	} catch (e) {
		if (!confirmAttempted) {
			event.paymentFailed({reason: 'fail'});
		}
		Bark.hideLoading();
		$modal.modal('show');
		showModalErrorText($modal, _t('seller_common:sofort-payment.error.invalid-request'));
		options.paymentOptions?.onError?.();
		console.error('failed to confirm');
		console.error(e);
	}
}

export const getReturnUrl = (event, options) => {
	const pidParam = options.paymentOptions?.projectId ? `&prx=${options.paymentOptions.projectId}` : ""
	const packTypeParam = options.paymentOptions?.packType ? `&packType=${options.paymentOptions.packType}` : "";

	return `${location.protocol}//${location.host}${location.pathname}?pm_type=${event.expressPaymentType}${pidParam}${packTypeParam}`
};

const attemptWalletPayment = async (event, elements, options) => {
	const initPaymentIntentResponse = await initPaymentIntent(
		event.expressPaymentType, // 'apple_pay'|'google_pay'|'paypal'
		options.paymentOptions.userId,
		options.paymentOptions.cpId,
		options.paymentOptions.nResponses,
		options.paymentOptions.code,
		options.paymentOptions.isUpsell,
	);

	const result = await stripe.confirmPayment({
		elements,
		clientSecret: initPaymentIntentResponse.clientSecret,
		confirmParams: {
			return_url: getReturnUrl(event, options)
		},
		redirect: 'if_required',
	});

	return {
		initPaymentIntentResponse,
		result,
	};
}

const attemptWalletSetup = async (event, elements, options) => {
	const { setupIntentId } = await initSetupIntent(options.paymentOptions.userId);

	const result = await stripe.confirmSetup({
		elements,
		clientSecret: setupIntentId,
		confirmParams: {
			return_url: getReturnUrl(event, options)
		},
		redirect: 'if_required'
	});

	return {
		setupIntentId,
		result,
	};
};
